import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { layoutAbc } from "../../lib/abcLayout";

// measure the free box a card leaves for the content (unaffected by preview scaling)
const useBox = () => {
  const ref = useRef(null);
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setBox({ w: el.clientWidth, h: el.clientHeight });
  }, []);
  return [ref, box];
};

const offscreen = (w) => {
  const el = document.createElement("div");
  el.style.cssText = `position:absolute;left:-99999px;top:0;width:${w}px;`;
  document.body.appendChild(el);
  return el;
};

const FILL = { flex: 1, minHeight: 0, width: "100%", overflow: "hidden" };

// standard notation laid out exactly like the PDF export, paginated to this card's box
export const FitNotation = ({ abc, zoom = 100, page = 0, onPages }) => {
  const [ref, box] = useBox();
  const [pages, setPages] = useState([]);

  useEffect(() => {
    if (!box || !abc) return;
    const el = offscreen(box.w);
    try {
      const { built } = layoutAbc(el, abc, box.w, box.h, zoom);
      setPages(built);
      onPages?.(Math.max(1, built.length));
    } catch (e) {
      setPages([]);
    } finally {
      el.remove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abc, zoom, box]);

  const lines = pages[page]?.lines || [];
  return (
    <div ref={ref} style={{ ...FILL, color: "#000", display: "flex", flexDirection: "column", justifyContent: pages.length > 1 ? "flex-start" : "center" }}>
      {lines.map((ln, j) => (
        <div key={j} style={{ width: box?.w, height: ln.h, overflow: "hidden", flexShrink: 0 }}>
          <div style={{ transform: `scale(${ln.s})`, transformOrigin: "top left" }} dangerouslySetInnerHTML={{ __html: ln.html }} />
        </div>
      ))}
    </div>
  );
};

// ABC text split into pages by real line heights at the chosen size
export const FitAbcText = ({ abc, zoom = 100, page = 0, onPages, base = 27, style }) => {
  const [ref, box] = useBox();
  const [pages, setPages] = useState([]);
  const font = { fontFamily: "ui-monospace, Menlo, monospace", fontSize: (base * zoom) / 100, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" };

  useEffect(() => {
    if (!box || !abc) return;
    const el = offscreen(box.w);
    Object.assign(el.style, { ...font, fontSize: `${font.fontSize}px` });
    const rows = abc.split("\n");
    const built = [];
    let cur = [];
    let y = 0;
    rows.forEach((r) => {
      const d = document.createElement("div");
      d.textContent = r || " ";
      el.appendChild(d);
      const h = d.getBoundingClientRect().height;
      if (y + h > box.h && cur.length) {
        built.push(cur);
        cur = [];
        y = 0;
      }
      cur.push(r);
      y += h;
    });
    if (cur.length) built.push(cur);
    el.remove();
    setPages(built);
    onPages?.(Math.max(1, built.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abc, zoom, box]);

  return (
    <div style={{ ...style, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <div ref={ref} style={{ ...FILL, ...font }}>
        {(pages[page] || []).join("\n")}
      </div>
    </div>
  );
};
